import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

// Staff-portal Investigations view for one agency: seats and pool, each
// investigator's usage, every investigation with its counts, and a monthly
// trend with a cost-of-goods estimate. This is the one place the real
// numbers show; customers only ever see "most" / "all" of the hours used.

type Bucket = {
  calls_uploaded: number;
  audio_minutes: number;
  files_uploaded: number;
  investigations_created: number;
  chat_messages: number;
  tokens_in: number;
  tokens_out: number;
};

type Insights = {
  seats: {
    purchased: number;
    assigned: number;
    source: string | null;
    status: string | null;
    billingInterval: "month" | "year";
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
  } | null;
  pool: {
    period: { interval: "month" | "year"; label: string };
    minutesUsed: number;
    allowanceMinutes: number;
    seatMinutes: number;
    creditMinutes: number;
    pct: number;
  } | null;
  totals: {
    investigations: number;
    activeInvestigations: number;
    leadsTotal: number;
    leadsConfirmed: number;
    period: Bucket & { estimatedCost: number };
    allTime: Bucket & { estimatedCost: number };
    unseatedUsers: number;
  };
  investigators: {
    userId: string;
    name: string | null;
    email: string;
    grantedAt: string;
    allowanceMinutes: number | null;
    investigationsOwned: number;
    period: Bucket;
    allTime: Bucket;
    estimatedCostPeriod: number;
    estimatedCostAllTime: number;
    lastActive: string | null;
  }[];
  investigations: {
    id: string;
    name: string;
    caseNumber: string | null;
    status: "active" | "archived";
    createdAt: string;
    briefGeneratedAt: string | null;
    owner: { id: string; name: string | null; email: string | null };
    files: number;
    calls: number;
    leadsOpen: number;
    leadsConfirmed: number;
    leadsTotal: number;
    members: number;
    chats: number;
  }[];
  months: (Bucket & { month: string; estimatedCost: number })[];
  costAssumptions: { transcribePerMinute: number; inputPerMtok: number; outputPerMtok: number };
};

const hours = (min: number) => (min / 60).toFixed(min >= 600 ? 0 : 1);
const usd = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString() : "—");
const monthLabel = (ym: string) => new Date(`${ym}-01T12:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const th = "text-xs font-semibold text-base-content/60 uppercase tracking-wide";
const td = "border-b border-base-200 dark:border-base-300 text-sm";
const num = "text-right tabular-nums";

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
  <div className="rounded-lg bg-base-200 px-4 py-3">
    <p className="m-0 text-xs text-base-content/60">{label}</p>
    <p className="m-0 text-2xl font-bold text-base-content leading-tight">{value}</p>
    {sub && <p className="m-0 text-xs text-base-content/60 mt-0.5">{sub}</p>}
  </div>
);

const AgencyInvestigationsTab: React.FC<{ agencyId: string; adminEmail: string }> = ({ agencyId, adminEmail }) => {
  const [data, setData] = useState<Insights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get<Insights>(`/api/agency-management/agencies/${agencyId}/investigations`, { headers: { "x-admin-email": adminEmail } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || "Failed to load investigations"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agencyId, adminEmail]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }
  if (error || !data) return <div className="text-center py-12 text-red-600 dark:text-red-400">{error || "No data"}</div>;

  const { seats, pool, totals } = data;
  const span = pool?.period.interval === "year" ? "billing year" : "month";
  const noAddon = !seats || seats.purchased === 0;

  return (
    <div className="space-y-6">
      {noAddon && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400 px-4 py-2 text-sm">
          This agency has no Investigations seats. Set seats from the Edit Agency dialog to enable the add-on.
          {totals.investigations > 0 && " Historical usage below is from a previous seat allocation."}
        </div>
      )}

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Stat
          label="Investigator seats"
          value={seats ? `${seats.assigned} / ${seats.purchased}` : "0"}
          sub={seats ? `${seats.billingInterval === "year" ? "annual" : "monthly"} · ${seats.source || "—"}${seats.currentPeriodEnd ? ` · renews ${date(seats.currentPeriodEnd)}` : ""}` : undefined}
        />
        <Stat
          label={`Pool this ${span}`}
          value={pool ? `${pool.pct}%` : "—"}
          sub={pool ? `${hours(pool.minutesUsed)} of ${hours(pool.allowanceMinutes)} h${pool.creditMinutes ? ` (${hours(pool.creditMinutes)} h from packs)` : ""}` : "no pool"}
        />
        <Stat label="Investigations" value={totals.investigations} sub={`${totals.activeInvestigations} active`} />
        <Stat label={`Calls this ${span}`} value={totals.period.calls_uploaded} sub={`${totals.allTime.calls_uploaded} all time`} />
        <Stat label={`Audio hours this ${span}`} value={hours(totals.period.audio_minutes)} sub={`${hours(totals.allTime.audio_minutes)} h all time`} />
        <Stat label="Leads flagged" value={totals.leadsTotal} sub={`${totals.leadsConfirmed} confirmed`} />
        <Stat label={`Chat questions this ${span}`} value={totals.period.chat_messages} sub={`${totals.allTime.chat_messages} all time`} />
        <Stat label={`Est. cost this ${span}`} value={usd(totals.period.estimatedCost)} sub={`${usd(totals.allTime.estimatedCost)} all time`} />
      </div>

      {/* Investigators */}
      <div>
        <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-2">
          Investigators ({data.investigators.length})
          {totals.unseatedUsers > 0 && (
            <span className="ml-2 text-xs font-normal text-base-content/60">+ {totals.unseatedUsers} former seat holder{totals.unseatedUsers === 1 ? "" : "s"} with usage</span>
          )}
        </h4>
        {data.investigators.length === 0 ? (
          <p className="text-sm text-base-content/60">No officers hold an investigator seat. Assign one from the Officers tab.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead className="border-b border-base-300">
                <tr>
                  <th className={th}>Investigator</th>
                  <th className={`${th} ${num}`}>Investigations</th>
                  <th className={`${th} ${num}`}>Calls ({span})</th>
                  <th className={`${th} ${num}`}>Audio h ({span})</th>
                  <th className={`${th} ${num}`}>Files ({span})</th>
                  <th className={`${th} ${num}`}>Chats ({span})</th>
                  <th className={`${th} ${num}`}>Seat used</th>
                  <th className={`${th} ${num}`}>Calls (all)</th>
                  <th className={`${th} ${num}`}>Audio h (all)</th>
                  <th className={`${th} ${num}`}>Est. cost (all)</th>
                  <th className={th}>Last active</th>
                  <th className={th}>Seat since</th>
                </tr>
              </thead>
              <tbody>
                {[...data.investigators]
                  .sort((a, b) => b.period.audio_minutes - a.period.audio_minutes || b.allTime.audio_minutes - a.allTime.audio_minutes)
                  .map((inv, i) => {
                    const seatMinutes = (inv.allowanceMinutes ?? 3000) * (pool?.period.interval === "year" ? 12 : 1);
                    const seatPct = Math.round((inv.period.audio_minutes / Math.max(1, seatMinutes)) * 100);
                    return (
                      <tr key={inv.userId} className={i % 2 === 0 ? "bg-base-100" : "bg-base-200"}>
                        <td className={td}>
                          <div className="font-medium text-base-content">{inv.name || "—"}</div>
                          <div className="text-xs text-base-content/60">{inv.email}</div>
                        </td>
                        <td className={`${td} ${num}`}>{inv.investigationsOwned}</td>
                        <td className={`${td} ${num}`}>{inv.period.calls_uploaded}</td>
                        <td className={`${td} ${num}`}>{hours(inv.period.audio_minutes)}</td>
                        <td className={`${td} ${num}`}>{inv.period.files_uploaded}</td>
                        <td className={`${td} ${num}`}>{inv.period.chat_messages}</td>
                        <td className={`${td} ${num}`}>
                          <span className={seatPct >= 150 ? "text-red-600 dark:text-red-400 font-semibold" : seatPct >= 100 ? "text-amber-700 dark:text-amber-400 font-semibold" : ""}>{seatPct}%</span>
                          <span className="text-xs text-base-content/50"> of a seat</span>
                        </td>
                        <td className={`${td} ${num}`}>{inv.allTime.calls_uploaded}</td>
                        <td className={`${td} ${num}`}>{hours(inv.allTime.audio_minutes)}</td>
                        <td className={`${td} ${num}`}>{usd(inv.estimatedCostAllTime)}</td>
                        <td className={td}>{date(inv.lastActive)}</td>
                        <td className={td}>{date(inv.grantedAt)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Investigations */}
      <div>
        <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-2">Investigations ({data.investigations.length})</h4>
        {data.investigations.length === 0 ? (
          <p className="text-sm text-base-content/60">No investigations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead className="border-b border-base-300">
                <tr>
                  <th className={th}>Investigation</th>
                  <th className={th}>Owner</th>
                  <th className={th}>Status</th>
                  <th className={`${th} ${num}`}>Members</th>
                  <th className={`${th} ${num}`}>Files</th>
                  <th className={`${th} ${num}`}>Calls</th>
                  <th className={`${th} ${num}`}>Leads</th>
                  <th className={`${th} ${num}`}>Chats</th>
                  <th className={th}>Created</th>
                  <th className={th}>Last brief</th>
                </tr>
              </thead>
              <tbody>
                {data.investigations.map((inv, i) => (
                  <tr key={inv.id} className={i % 2 === 0 ? "bg-base-100" : "bg-base-200"}>
                    <td className={td}>
                      {/* Opens the real investigation screens, read-only, in the app. Needs an
                          app login with a staff email (the server checks that session, not the
                          portal password). */}
                      <a
                        href={`/investigations/${inv.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 dark:text-blue-300 hover:underline inline-flex items-center gap-1"
                        title="Open read-only in a new tab"
                      >
                        {inv.name}
                        <ArrowTopRightOnSquareIcon className="size-3.5" />
                      </a>
                      {inv.caseNumber && <div className="text-xs text-base-content/60">#{inv.caseNumber}</div>}
                    </td>
                    <td className={td}>
                      <div>{inv.owner.name || "—"}</div>
                      <div className="text-xs text-base-content/60">{inv.owner.email}</div>
                    </td>
                    <td className={td}>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${inv.status === "active" ? "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400" : "bg-base-300 text-base-content/70"}`}>{inv.status}</span>
                    </td>
                    <td className={`${td} ${num}`}>{inv.members}</td>
                    <td className={`${td} ${num}`}>{inv.files}</td>
                    <td className={`${td} ${num}`}>{inv.calls}</td>
                    <td className={`${td} ${num}`}>
                      {inv.leadsTotal}
                      {inv.leadsTotal > 0 && <span className="text-xs text-base-content/50"> ({inv.leadsOpen} open, {inv.leadsConfirmed} confirmed)</span>}
                    </td>
                    <td className={`${td} ${num}`}>{inv.chats}</td>
                    <td className={td}>{date(inv.createdAt)}</td>
                    <td className={td}>{date(inv.briefGeneratedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly trend */}
      {data.months.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 dark:text-base-content/80 mb-2">By month</h4>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead className="border-b border-base-300">
                <tr>
                  <th className={th}>Month</th>
                  <th className={`${th} ${num}`}>Calls</th>
                  <th className={`${th} ${num}`}>Audio h</th>
                  <th className={`${th} ${num}`}>Files</th>
                  <th className={`${th} ${num}`}>Investigations created</th>
                  <th className={`${th} ${num}`}>Chats</th>
                  <th className={`${th} ${num}`}>Tokens in</th>
                  <th className={`${th} ${num}`}>Tokens out</th>
                  <th className={`${th} ${num}`}>Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m, i) => (
                  <tr key={m.month} className={i % 2 === 0 ? "bg-base-100" : "bg-base-200"}>
                    <td className={td}>{monthLabel(m.month)}</td>
                    <td className={`${td} ${num}`}>{m.calls_uploaded}</td>
                    <td className={`${td} ${num}`}>{hours(m.audio_minutes)}</td>
                    <td className={`${td} ${num}`}>{m.files_uploaded}</td>
                    <td className={`${td} ${num}`}>{m.investigations_created}</td>
                    <td className={`${td} ${num}`}>{m.chat_messages}</td>
                    <td className={`${td} ${num}`}>{m.tokens_in.toLocaleString()}</td>
                    <td className={`${td} ${num}`}>{m.tokens_out.toLocaleString()}</td>
                    <td className={`${td} ${num}`}>{usd(m.estimatedCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-base-content/50 mt-1 m-0">
            Cost estimate: Transcribe ${data.costAssumptions.transcribePerMinute}/min, model input ${data.costAssumptions.inputPerMtok}/M tokens, output ${data.costAssumptions.outputPerMtok}/M tokens (`INVESTIGATIONS_COST_*` env). Excludes Textract, embeddings, and storage.
          </p>
        </div>
      )}
    </div>
  );
};

export default AgencyInvestigationsTab;
